import { useEffect, useRef, useState } from "react"
import { isWcmEditor } from "../../../../../config/serverConfig"
import { LiveVideoPlayer } from "../../liveVideoPlayer/components/liveVideoPlayer"
import { videoSourceOption } from "../../../topStory/windows/contentWindow/videoLiveSourceSelector"

interface TopStoryMultiPlayerWrapperProps {
    videoTitle: string
    videoAutoPlay: boolean
    videoMuteSound: boolean
    liveVideoSource: videoSourceOption
    videoShowSoundBtn: boolean
    liveIndicationText: string
    videoShowControlPanel: boolean
    observedComponentClassName: string
    isMobileWeb?: boolean
}

export const TopStoryMultiPlayerWrapper = (props: TopStoryMultiPlayerWrapperProps) => {

    const { liveVideoSource, videoTitle, videoAutoPlay, videoMuteSound, videoShowSoundBtn, videoShowControlPanel, observedComponentClassName, isMobileWeb, liveIndicationText } = props
    const [isPlayerOpen, setIsPlayerOpen] = useState(false)
    const [userPausedVideo, setUserPausedVideo] = useState(false)
    const [hasComponentIntersectedOnce, setHasComponentIntersectedOnce] = useState(false)
    const [hasTopStoryVideoPlayedOnce, setHasTopStoryVideoPlayedOnce] = useState(false)
    const isPlayerOpenRef: any = useRef<boolean>(isPlayerOpen)
    const floatingPlayerRef = useRef<any>(null)
    const topStoryPlayerRef: any = useRef<any>(null)
    const isProgrammaticPause: any = useRef<boolean>(false)

    useEffect(() => {
        isPlayerOpenRef.current = isPlayerOpen
    }, [isPlayerOpen])

    const handleTopStoryVideoPaused = () => {
        if (isProgrammaticPause.current) {
            isProgrammaticPause.current = false
            return
        }
        setUserPausedVideo(true);
    }

    const handleTopStoryVideoPlayedOnce = () => {
        if (!hasTopStoryVideoPlayedOnce) {
            setHasTopStoryVideoPlayedOnce(true);
        }
    }

    useEffect(() => {
        if (isWcmEditor()) return;

        const element = document.querySelector(`.${observedComponentClassName}`);
        const videoElement = element && element.querySelector('video');
        if (videoElement) {
            topStoryPlayerRef.current = videoElement;
            videoElement.addEventListener('pause', handleTopStoryVideoPaused)
            videoElement.addEventListener('play', handleTopStoryVideoPlayedOnce)
        }

        return () => {
            if (videoElement) {
                videoElement.removeEventListener('pause', handleTopStoryVideoPaused)
                videoElement.removeEventListener('play', handleTopStoryVideoPlayedOnce)
            }
        }
    }, [handleTopStoryVideoPaused])

    useEffect(() => {
        if (isWcmEditor()) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (topStoryPlayerRef.current && floatingPlayerRef.current) {
                    if (entries[0].isIntersecting) {
                        if (!hasComponentIntersectedOnce) {
                            setHasComponentIntersectedOnce(true);
                            topStoryPlayerRef.current.muted = true
                        }

                        if (hasTopStoryVideoPlayedOnce && !userPausedVideo) {
                            topStoryPlayerRef.current.play();
                        }

                        if (isPlayerOpenRef.current) {
                            setIsPlayerOpen(false);
                            floatingPlayerRef.current.pause();
                        }
                    } else {
                        if (hasComponentIntersectedOnce) {
                            if (!topStoryPlayerRef.current.paused) {
                                isProgrammaticPause.current = true
                                topStoryPlayerRef.current.pause();
                            }

                            if (!topStoryPlayerRef.current.muted) {
                                setIsPlayerOpen(true);
                                floatingPlayerRef.current.play();
                            }
                        }
                    }
                }
            },
            { threshold: 0.5 }
        );

        const element = document.querySelector(`.${observedComponentClassName}`);
        if (element) {
            observer.observe(element);
        }

        return () => {
            if (observer) {
                observer.disconnect();
            }
        }
    }, [hasComponentIntersectedOnce, observedComponentClassName, hasTopStoryVideoPlayedOnce, userPausedVideo]);

    const onIsPlayerOpenChange = (isOpen: boolean) => {
        setIsPlayerOpen(isOpen)
    }

    return (
        <div className="top-story-multi-player-wrapper">
            <LiveVideoPlayer
                videoTitle={videoTitle}
                isMobileWeb={isMobileWeb}
                thisLocation={"top story"}
                isMinimizedVisible={false}
                videoAutoPlay={videoAutoPlay}
                videoMuteSound={videoMuteSound}
                liveVideoSource={liveVideoSource}
                externalIsOpenTrigger={isPlayerOpen}
                videoShowSoundBtn={videoShowSoundBtn}
                liveIndicationText={liveIndicationText}
                playerRefForExternal={floatingPlayerRef}
                videoShowControlPanel={videoShowControlPanel}
                onExternalIsOpenTriggerChange={onIsPlayerOpenChange}
            />
        </div>
    )
}

TopStoryMultiPlayerWrapper.siteScriptName = "TopStoryMultiPlayerWrapper"